import { useLogto } from '@logto/react';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { createApiClient, type CustomerDetail, type CustomerListItem } from '../api/client';
import { bookingStatusLabel, consentLabel, erasureStatusLabel, filterCustomers, identityKindLabel } from '../customers';
import { ErrorBanner } from '../ui/ErrorBanner';
import { channelLabel, formatDateTime, formatEur } from '../ui/format';

export function Customers() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  return selectedId ? (
    <CustomerDetailView id={selectedId} onBack={() => setSelectedId(null)} />
  ) : (
    <CustomerList onOpen={setSelectedId} />
  );
}

function ConsentBadge({ state }: { state: string | null }) {
  const cls = state === 'Granted' ? 'badge badge-ok' : state === 'Withdrawn' ? 'badge badge-off' : 'badge badge-muted';
  return <span className={cls}>{consentLabel(state)}</span>;
}

function IdentityChips({ identities }: { identities: { kind: string; value: string }[] }) {
  if (identities.length === 0) {
    return <span className="subtle">—</span>;
  }
  return (
    <div className="chips">
      {identities.map((i) => (
        <span key={`${i.kind}:${i.value}`} className="chip">{identityKindLabel(i.kind)} · {i.value}</span>
      ))}
    </div>
  );
}

function CustomerList({ onOpen }: { onOpen: (id: string) => void }) {
  const { getAccessToken } = useLogto();
  const api = useMemo(() => createApiClient(getAccessToken), [getAccessToken]);
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const load = useCallback(async () => {
    try {
      setCustomers(await api.getCustomers());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = filterCustomers(customers, query);

  async function createCustomer(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      // 201 for a new customer or 200 for an existing identity: either way open that one, never a duplicate.
      const created = await api.createCustomer({
        name: name.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null
      });
      onOpen(created.id);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section className="page">
      <header className="page-head">
        <h1>Clientes</h1>
        <p className="subtle">Quem reserva consigo, com contactos, consentimento de marketing e pedidos de remoção.</p>
      </header>
      <ErrorBanner message={error} />

      <section className="card">
        <div className="list-toolbar">
          <input
            className="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Procurar por nome, email ou telefone"
            aria-label="Procurar clientes"
          />
          <button className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Fechar' : 'Novo cliente'}
          </button>
        </div>

        {showForm ? (
          <form className="inline-form" onSubmit={createCustomer}>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefone" />
            <button className="btn btn-primary" type="submit" disabled={!name.trim() && !email.trim() && !phone.trim()}>
              Criar
            </button>
          </form>
        ) : null}

        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Contactos</th>
                <th>Marketing</th>
                <th className="num">Reservas</th>
                <th className="num">Valor</th>
                <th>Última reserva</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.id} className="row-link" onClick={() => onOpen(c.id)}>
                  <td>{c.displayName}</td>
                  <td><IdentityChips identities={c.identities} /></td>
                  <td><ConsentBadge state={c.marketingConsent} /></td>
                  <td className="num">{c.bookingsCount}</td>
                  <td className="num money">{formatEur(c.lifetimeValueEur)}</td>
                  <td>{c.lastBookingAt ? formatDateTime(c.lastBookingAt) : '—'}</td>
                </tr>
              ))}
              {visible.length === 0 ? (
                <tr><td colSpan={6} className="subtle">Sem clientes para mostrar.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}

function CustomerDetailView({ id, onBack }: { id: string; onBack: () => void }) {
  const { getAccessToken } = useLogto();
  const api = useMemo(() => createApiClient(getAccessToken), [getAccessToken]);
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [consentState, setConsentState] = useState('Granted');
  const [mechanism, setMechanism] = useState('');
  const [proof, setProof] = useState('');
  const [erasing, setErasing] = useState(false);

  const load = useCallback(async () => {
    try {
      setDetail(await api.getCustomer(id));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [api, id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function addConsent(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(null);
    try {
      await api.appendConsent(id, {
        state: consentState,
        mechanism: mechanism.trim() || 'back-office',
        jurisdiction: 'PT',
        proof: proof.trim() || null
      });
      setMechanism('');
      setProof('');
      setSaved('Consentimento registado.');
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function requestErasure() {
    if (!window.confirm('Registar um pedido de remoção para este cliente? A ação fica registada e não pode ser anulada.')) {
      return;
    }
    setError(null);
    setSaved(null);
    setErasing(true);
    try {
      const er = await api.requestErasure(id);
      setSaved(`Pedido de remoção registado, prazo ${formatDateTime(er.dueBy)}.`);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setErasing(false);
    }
  }

  if (!detail) {
    return (
      <section className="page">
        <button className="btn btn-ghost back-link" onClick={onBack}>← Clientes</button>
        <ErrorBanner message={error} />
        {error ? null : <p className="subtle">A carregar…</p>}
      </section>
    );
  }

  return (
    <section className="page">
      <button className="btn btn-ghost back-link" onClick={onBack}>← Clientes</button>
      <header className="page-head">
        <h1>{detail.displayName}</h1>
        <IdentityChips identities={detail.identities} />
      </header>
      <ErrorBanner message={error} />
      {saved ? <div className="alert-ok" role="status">{saved}</div> : null}

      <section className="card">
        <h2>Reservas</h2>
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr><th>Quando</th><th>Canal</th><th className="num">Pax</th><th className="num">Valor</th><th>Estado</th></tr>
            </thead>
            <tbody>
              {detail.bookings.map((b) => (
                <tr key={b.id}>
                  <td>{formatDateTime(b.startsAt)}</td>
                  <td>{channelLabel(b.channel)}</td>
                  <td className="num">{b.pax}</td>
                  <td className="num money">{formatEur(b.customerPaidEur)}</td>
                  <td>{bookingStatusLabel(b.status)}</td>
                </tr>
              ))}
              {detail.bookings.length === 0 ? <tr><td colSpan={5} className="subtle">Sem reservas.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <h2>Consentimento de marketing</h2>
        <p><ConsentBadge state={detail.consentHistory[0]?.state ?? null} /></p>
        <div className="table-scroll">
          <table className="data">
            <thead><tr><th>Estado</th><th>Via</th><th>Jurisdição</th><th>Quando</th><th>Prova</th></tr></thead>
            <tbody>
              {detail.consentHistory.map((c) => (
                <tr key={c.id}>
                  <td>{c.state === 'Granted' ? 'Concedido' : 'Retirado'}</td>
                  <td>{c.mechanism}</td>
                  <td>{c.jurisdiction}</td>
                  <td>{formatDateTime(c.recordedAt)}</td>
                  <td className="subtle">{c.proof ?? '—'}</td>
                </tr>
              ))}
              {detail.consentHistory.length === 0 ? <tr><td colSpan={5} className="subtle">Sem registos.</td></tr> : null}
            </tbody>
          </table>
        </div>
        <form className="inline-form" onSubmit={addConsent}>
          <select value={consentState} onChange={(e) => setConsentState(e.target.value)} aria-label="Estado do consentimento">
            <option value="Granted">Concedido</option>
            <option value="Withdrawn">Retirado</option>
          </select>
          <input value={mechanism} onChange={(e) => setMechanism(e.target.value)} placeholder="Via (ex. formulário web)" />
          <input value={proof} onChange={(e) => setProof(e.target.value)} placeholder="Prova (opcional)" />
          <button className="btn btn-primary" type="submit">Registar consentimento</button>
        </form>
      </section>

      <section className="card">
        <h2>Pedidos de remoção</h2>
        <div className="table-scroll">
          <table className="data">
            <thead><tr><th>Pedido em</th><th>Prazo</th><th>Estado</th></tr></thead>
            <tbody>
              {detail.erasureRequests.map((er) => (
                <tr key={er.id}>
                  <td>{formatDateTime(er.requestedAt)}</td>
                  <td>{formatDateTime(er.dueBy)}</td>
                  <td>{erasureStatusLabel(er.status)}</td>
                </tr>
              ))}
              {detail.erasureRequests.length === 0 ? <tr><td colSpan={3} className="subtle">Nenhum.</td></tr> : null}
            </tbody>
          </table>
        </div>
        <button className="btn" onClick={() => void requestErasure()} disabled={erasing}>
          {erasing ? 'A registar…' : 'Registar pedido de remoção'}
        </button>
      </section>
    </section>
  );
}
